// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscription, TySubscriptionStatus } from "@/types";
import { formatDate, formatMoney, pressToOpen, readLangValue, rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import StatusChip, { type TyChipTone } from "@/UI/atoms/StatusChip";
import "../row.css";
import "./styles.css";
// --- Constants ----------------------------------------------------------------------------------
const STATUS_TONES: Record<TySubscriptionStatus, TyChipTone> = {
    pending: "warn",
    active: "good",
    coming: "dark",
    grace: "warn",
    ended: "neutral",
    cancelled: "neutral",
};
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    subscription: TySubscription;
    /** Opens the company it belongs to — a period has no screen of its own and needs none. */
    onOpen: () => void;
    /** Confirming the transfer. Absent unless this row is actually waiting for one. */
    onConfirm?: () => void;
    /**
     * Confirming a **storage** transfer, which is a separate one. A company on a live period can
     * ask for a pack mid-period, so a row that is not itself waiting for money can still have
     * somebody waiting on it.
     */
    onConfirmStorage?: () => void;
    /**
     * Absent where the card is a **preview** of a list that owns the actions — the home screen shows
     * what is running out and hands off to the billing queue rather than growing a second copy of its
     * menu. In the queue itself it is always passed.
     */
    onActions?: (anchor: DOMRect) => void;
};

/**
 * One paid period, or one request for one. Billing is manual, so a row in the first tab is not a
 * report — it is a decision somebody has to take: confirm this transfer, or let the nightly job
 * cancel it.
 *
 * The amount is the sold price, and it is the figure a bank transfer has to match — so after the
 * company's name it is the loudest thing on the card.
 */
export default function SubscriptionCard({ subscription, onOpen, onConfirm, onConfirmStorage, onActions }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const organization = subscription.organization;
    const name = organization ? readLangValue(organization.name, lang) || organization.slug : "";
    const isPending = subscription.status === "pending";
    /** Paid packs and asked-for packs are two different facts, so they are two different chips. */
    const paidAddons = subscription.addons.filter((addon) => addon.status !== "pending" && addon.status !== "cancelled");
    const waitingAddons = subscription.addons.filter((addon) => addon.status === "pending");

    /** One line: how long the period is, then the date that matters for the state it is in. */
    const meta = isPending
        ? `${t.subscriptions.periods[subscription.period]} · ${t.subscriptions.dueOn.replace("{date}", formatDate(subscription.paymentDueAt, lang))}`
        : `${t.subscriptions.periods[subscription.period]} · ${formatDate(subscription.startsAt, lang)} → ${formatDate(subscription.endsAt, lang)}`;

    return (
        <article
            className="row-card row-card--open"
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={onActions ? rightClickToOpen(onActions) : undefined}
        >
            <span className={`row-card__icon${isPending ? " subscription-card__icon--pending" : ""}`}>
                <Icon name="subscription" size={20} />
            </span>

            <div className="row-card__body">
                <span className="row-card__title">{name}</span>
                <span className="row-card__meta row-card__ltr">{meta}</span>
                <div className="row-card__chips">
                    <StatusChip size="sm" tone={STATUS_TONES[subscription.status]} label={t.subscriptions.statuses[subscription.status]} />
                    {paidAddons.length > 0 && (
                        <StatusChip size="sm" tone="accent" label={t.subscriptions.addonOf.replace("{count}", String(paidAddons.length))} />
                    )}
                    {waitingAddons.length > 0 && (
                        <StatusChip size="sm" tone="warn" label={t.subscriptions.addonWaiting.replace("{count}", String(waitingAddons.length))} />
                    )}
                </div>
            </div>

            <div className="row-card__figure">
                <span className="row-card__amount">{formatMoney(subscription.price)}</span>
                <span className="row-card__unit">{t.subscriptions.currency}</span>
            </div>

            <div className="row-card__actions">
                {/* A row can be waiting on two different transfers; the period's is the louder. */}
                {!onConfirm && onConfirmStorage && (
                    <button
                        type="button"
                        className="row-card__main"
                        onClick={(event) => {
                            event.stopPropagation();
                            onConfirmStorage();
                        }}
                    >
                        <Icon name="drive" size={16} />
                        <span>{t.subscriptions.confirmStorage}</span>
                    </button>
                )}

                {onConfirm && (
                    <button
                        type="button"
                        className="row-card__main"
                        onClick={(event) => {
                            event.stopPropagation();
                            onConfirm();
                        }}
                    >
                        <Icon name="check" size={16} />
                        <span>{t.subscriptions.confirmPayment}</span>
                    </button>
                )}

                {onActions && (
                    <button
                        type="button"
                        className="row-card__more"
                        onClick={(event) => {
                            event.stopPropagation();
                            onActions(event.currentTarget.getBoundingClientRect());
                        }}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={18} />
                    </button>
                )}
            </div>
        </article>
    );
}
